// AboutLocalVoiceDialog.jsx
// Renders the About LocalVoice dialog content with optional email capture.
import { useState } from 'react';
import DialogBox from './DialogBox';
import { submitFeedbackAsync } from '../utils/feedback';

export default function AboutLocalVoiceDialog({ open, onClose }) {
  const [feedback, setFeedback] = useState('');
  const [email, setEmail] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFeedbackChange = (event) => {
    setFeedback(event.target.value);
    if (statusMessage) {
      setStatusMessage('');
    }
  };

  const handleEmailChange = (event) => {
    setEmail(event.target.value);
    if (statusMessage) {
      setStatusMessage('');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;

    const trimmedFeedback = feedback.trim();
    const trimmedEmail = email.trim();

    if (!trimmedFeedback) {
      setStatusMessage('Please share a quick detail before submitting.');
      return;
    }

    setIsSubmitting(true);
    setStatusMessage('');

    try {
      await submitFeedbackAsync({
        origin: 'about-dialog',
        feedbackText: trimmedFeedback,
        emailAddress: trimmedEmail,
      });
      setStatusMessage('Thanks for the feedback!');
      setFeedback('');
      setEmail('');
    } catch (error) {
      console.error('Failed to submit feedback.', error);
      setStatusMessage('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DialogBox open={open} onClose={onClose} title="About LocalVoice">
      <form onSubmit={handleSubmit} className="space-y-4 text-slate-700">
        <p className="text-base leading-6 text-slate-600">
          LocalVoice is a non-commercial, non-partisan project that aims to help make it easier for people to connect with their local
          community and representatives. Our hope is to build bonds and strengthen everyday people's voice in their
          government.
        </p>
        <label htmlFor="about-feedback" className="block text-sm font-semibold text-slate-900">
          We'd love feedback!
        </label>
        <textarea
          id="about-feedback"
          rows={4}
          placeholder="Have ideas for how LocalVoice can help your community?"
          className="mt-2 w-full resize-none rounded-2xl border border-slate-300 bg-white px-4 py-3 text-base text-slate-900 placeholder:text-slate-400 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-200"
          value={feedback}
          onChange={handleFeedbackChange}
        />
        <label htmlFor="about-email" className="block text-sm font-semibold text-slate-900">
          Email address (optional)
        </label>
        <input
          id="about-email"
          type="email"
          placeholder="you@example.com"
          className="mt-1 w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-base text-slate-900 placeholder:text-slate-400 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-200"
          value={email}
          onChange={handleEmailChange}
        />
        {statusMessage && (
          <p className="text-sm font-medium text-indigo-600" role="status">
            {statusMessage}
          </p>
        )}
        <button
          type="submit"
          className="w-full rounded-2xl bg-indigo-600 px-4 py-3 text-base font-semibold text-white shadow-sm transition hover:bg-indigo-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Sending…' : 'Share feedback'}
        </button>
      </form>
    </DialogBox>
  );
}
